import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { useLang } from '../i18n';
import api from '../api';

// Mirror of the backend _USERNAME_RE so we skip pointless network checks.
const USERNAME_RE = /^[A-Za-z0-9_.-]{3,30}$/;

/**
 * Live username availability indicator with one-tap suggestions.
 *
 * - Debounces an availability check against the public /auth/username-check.
 * - When the desired username is taken, shows close, available alternatives as
 *   clickable chips; clicking one calls `onPick(name)`.
 * - `currentUsername` (optional) suppresses feedback while the value equals the
 *   user's own existing username (nothing to change).
 */
export const UsernameAvailability = ({ value, currentUsername = '', onPick }) => {
  const { lang } = useLang();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);
  const [state, setState] = useState({ status: 'idle', suggestions: [] });
  const timer = useRef(null);

  useEffect(() => {
    const v = (value || '').trim();
    if (timer.current) clearTimeout(timer.current);
    if (!v) { setState({ status: 'idle', suggestions: [] }); return undefined; }
    if (currentUsername && v.toLowerCase() === currentUsername.toLowerCase()) {
      setState({ status: 'idle', suggestions: [] });
      return undefined;
    }
    if (!USERNAME_RE.test(v)) { setState({ status: 'invalid', suggestions: [] }); return undefined; }
    setState((s) => ({ status: 'checking', suggestions: s.suggestions }));
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.checkUsername(v);
        if (!data || !data.valid) setState({ status: 'invalid', suggestions: [] });
        else if (data.available) setState({ status: 'available', suggestions: [] });
        else setState({ status: 'taken', suggestions: data.suggestions || [] });
      } catch (e) {
        // Network hiccup: stay silent rather than block the form.
        setState({ status: 'idle', suggestions: [] });
      }
    }, 450);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [value, currentUsername]);

  if (state.status === 'idle' || state.status === 'invalid') return null;

  return (
    <div className="space-y-1.5" data-testid="username-availability">
      {state.status === 'checking' && (
        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground" data-testid="username-checking">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('Verfügbarkeit wird geprüft…', 'Checking availability…')}
        </p>
      )}

      {state.status === 'available' && (
        <p className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-600 dark:text-emerald-400" data-testid="username-available">
          <Check className="h-3.5 w-3.5" />
          {t('Benutzername ist verfügbar', 'Username is available')}
        </p>
      )}

      {state.status === 'taken' && (
        <div className="space-y-1.5" data-testid="username-taken">
          <p className="flex items-center gap-1.5 text-[12px] font-medium text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {t('Dieser Benutzername ist bereits vergeben', 'This username is already taken')}
          </p>
          {state.suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">{t('Vorschläge:', 'Suggestions:')}</span>
              {state.suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onPick && onPick(s)}
                  data-testid={`username-suggestion-${s}`}
                  className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-[12px] font-medium text-primary transition-colors duration-150 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
